/**
 * Self-test suite for view-once media.
 *
 * Mirrors iOS `ViewOnceMediaSelfTest.swift` case-for-case and adds
 * web-specific variants (base64 key-leak scan in raw IndexedDB JSON).
 *
 * All cases hit pure crypto + IndexedDB only — no Solid UI, no network.
 * Each store instance uses a unique DB name so cases are fully isolated
 * even when run in parallel.
 *
 * Cases:
 *   Crypto (1-3)
 *     1. sealForUpload → openCiphertext round-trip: decrypted matches plaintext.
 *     2. seal twice with same plaintext yields different ciphertext (nonce).
 *     3. openCiphertext with wrong key throws ViewOnceCryptoError.
 *
 *   Store / state machine (4-12)
 *     4. register → state `unviewed`, manifest present.
 *     5. register is idempotent (does not re-arm a consumed message).
 *     6. beginConsume on `unviewed` → returns manifest, state `viewing`.
 *     7. Second beginConsume throws ViewOnceWrongStateError (one-shot).
 *     8. finishConsume on `viewing` → state `viewed`, manifest null.
 *     9. finishConsume from `unviewed` throws ViewOnceWrongStateError.
 *    10. wipe from any non-terminal state → `wiped`, manifest null.
 *    11. wipe is idempotent.
 *    12. markExpired → `expired`, manifest null.
 *
 *   Hardening (13-17)
 *    13. After finishConsume, raw IndexedDB JSON contains zero key bytes
 *        (binary check) and zero base64-encoded key bytes.
 *    14. After wipe, same key-byte check.
 *    15. recoverInterruptedSessions: stale `viewing` → `viewed`, manifest gone.
 *    16. After consume, manifest accessor returns null.
 *    17. State persists across store re-instantiation (same DB name).
 *
 *   Coordinator (18-21)
 *     18. prepareForSend registers as `.outgoing` in `.unviewed`.
 *     19. End-to-end: prepareForSend → consume → finishConsume recovers plaintext.
 *     20. consume twice — second call throws.
 *     21. consume after wipe — throws ViewOnceWrongStateError (or NotFoundError).
 *
 * Registered with the global SelfTestRunner at module load time.
 * Auto-discovered by `selftest/index.ts` via `import.meta.glob("../**\/*SelfTest.ts")`.
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  sealForUpload,
  openCiphertext,
  generateKey,
  uint8ToBase64,
  ViewOnceCryptoError,
  VIEW_ONCE_MANIFEST_VERSION,
} from "./ViewOnceMedia";
import {
  ViewOnceStore,
  ViewOnceNotFoundError,
  ViewOnceWrongStateError,
} from "./ViewOnceStore";
import { ViewOnceCoordinator } from "./ViewOnceCoordinator";

// ── Registration ──────────────────────────────────────────────────────────────

SelfTestRunner.register({
  name: "View-once media",
  isVerified: true,
  run: async () =>
    buildSuiteResult([
      // Crypto
      await testSealOpenRoundTrip(),
      await testSealUsesUniqueNonce(),
      await testOpenWithWrongKeyFails(),
      // Store / state machine
      await testRegisterCreatesUnviewed(),
      await testRegisterIsIdempotent(),
      await testBeginConsumeAdvancesToViewing(),
      await testBeginConsumeFromViewingThrows(),
      await testFinishConsumeDropsManifest(),
      await testFinishConsumeFromUnviewedThrows(),
      await testWipeFromAnyNonTerminalState(),
      await testWipeIsIdempotent(),
      await testMarkExpiredDropsManifest(),
      // Hardening
      await testKeyBytesGoneFromIndexedDBAfterFinishConsume(),
      await testKeyBytesGoneFromIndexedDBAfterWipe(),
      await testRecoverInterruptedSessions(),
      await testManifestAccessorNilAfterConsume(),
      await testStatePersistsAcrossStoreInstances(),
      // Coordinator
      await testCoordPrepareRegistersOutgoing(),
      await testCoordEndToEndRoundTrip(),
      await testCoordConsumeTwiceThrows(),
      await testCoordConsumeAfterWipeThrows(),
    ]),
});

// ── Fixtures ──────────────────────────────────────────────────────────────────

let _storeSeq = 0;

/** Create a fresh isolated store for each test. */
function freshStore(): ViewOnceStore {
  return new ViewOnceStore(`kaether_view_once_test_${Date.now()}_${_storeSeq++}`);
}

/** Build a sample manifest with a fresh random key. */
function sampleManifest(id: string) {
  return {
    version: VIEW_ONCE_MANIFEST_VERSION as typeof VIEW_ONCE_MANIFEST_VERSION,
    mediaId: id,
    blobUrl: `https://example.invalid/${id}`,
    mimeType: "image/jpeg",
    byteLength: 1024,
    key: generateKey(),
    createdAt: new Date().toISOString(),
  };
}

/** Plaintext bytes helper. */
function text(s: string): Uint8Array {
  return new TextEncoder().encode(s);
}

// ── Crypto ────────────────────────────────────────────────────────────────────

async function testSealOpenRoundTrip(): Promise<TestCase> {
  const name = "seal → open round-trip recovers plaintext";
  try {
    const pt = text("Top secret media");
    const { ciphertext, manifest } = await sealForUpload(pt, "m1", "u", "image/jpeg");
    const recovered = await openCiphertext(ciphertext, manifest.key);
    const ok = recovered.length === pt.length && recovered.every((b, i) => b === pt[i]);
    return ok
      ? pass(name)
      : fail(name, `recovered ${recovered.length} bytes, want ${pt.length}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testSealUsesUniqueNonce(): Promise<TestCase> {
  const name = "seal uses fresh random nonce per call";
  try {
    const pt = text("same input");
    const { ciphertext: ct1 } = await sealForUpload(pt, "m1", "u", "image/jpeg");
    const { ciphertext: ct2 } = await sealForUpload(pt, "m2", "u", "image/jpeg");
    // Different keys + nonces → different ciphertexts with overwhelming probability.
    const ok = ct1.length !== ct2.length || ct1.some((b, i) => b !== ct2[i]);
    return ok
      ? pass(name)
      : fail(name, "ct1 === ct2 — either key or nonce reuse!");
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testOpenWithWrongKeyFails(): Promise<TestCase> {
  const name = "open with wrong key throws";
  try {
    const { ciphertext } = await sealForUpload(text("payload"), "m1", "u", "image/jpeg");
    const wrongKey = generateKey();
    try {
      await openCiphertext(ciphertext, wrongKey);
      return fail(name, "decryption succeeded with wrong key");
    } catch (e) {
      if (e instanceof ViewOnceCryptoError) return pass(name);
      return fail(name, `unexpected error type: ${String(e)}`);
    }
  } catch (e) {
    return fail(name, `seal failed: ${String(e)}`);
  }
}

// ── Store / state machine ──────────────────────────────────────────────────────

async function testRegisterCreatesUnviewed(): Promise<TestCase> {
  const name = "register creates .unviewed record with manifest";
  try {
    const store = freshStore();
    const manifest = sampleManifest("m1");
    const record = await store.register(manifest, "incoming");
    const ok =
      record.state === "unviewed" &&
      record.manifest !== null &&
      record.direction === "incoming";
    return ok
      ? pass(name)
      : fail(name, `state=${record.state} manifest=${record.manifest} dir=${record.direction}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testRegisterIsIdempotent(): Promise<TestCase> {
  const name = "register is idempotent (no re-arm after consume)";
  try {
    const store = freshStore();
    const m1 = sampleManifest("m1");
    await store.register(m1, "incoming");
    await store.beginConsume("m1");
    await store.finishConsume("m1");
    // Second register with different manifest must NOT rewind state.
    const alternate = {
      ...sampleManifest("m1"),
      blobUrl: "different",
      key: generateKey(),
    };
    await store.register(alternate, "incoming");
    const s = await store.state("m1");
    const ok = s === "viewed";
    return ok ? pass(name) : fail(name, `state after re-register=${s}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testBeginConsumeAdvancesToViewing(): Promise<TestCase> {
  const name = "beginConsume on .unviewed → .viewing, returns manifest key";
  try {
    const store = freshStore();
    const m = sampleManifest("m1");
    await store.register(m, "incoming");
    const returnedManifest = await store.beginConsume("m1");
    const s = await store.state("m1");
    const keyMatch =
      returnedManifest.key.length === m.key.length &&
      returnedManifest.key.every((b, i) => b === m.key[i]);
    const ok = s === "viewing" && keyMatch;
    return ok
      ? pass(name)
      : fail(name, `state=${s} keyMatch=${keyMatch}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testBeginConsumeFromViewingThrows(): Promise<TestCase> {
  const name = "second beginConsume throws ViewOnceWrongStateError (one-shot)";
  try {
    const store = freshStore();
    await store.register(sampleManifest("m1"), "incoming");
    await store.beginConsume("m1"); // → viewing
    try {
      await store.beginConsume("m1"); // should throw
      return fail(name, "key was returned a second time");
    } catch (e) {
      if (e instanceof ViewOnceWrongStateError && e.actual === "viewing") {
        return pass(name);
      }
      return fail(name, `wrong error type or actual state: ${String(e)}`);
    }
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testFinishConsumeDropsManifest(): Promise<TestCase> {
  const name = "finishConsume → .viewed, manifest null";
  try {
    const store = freshStore();
    await store.register(sampleManifest("m1"), "incoming");
    await store.beginConsume("m1");
    await store.finishConsume("m1");
    const s = await store.state("m1");
    const m = await store.manifest("m1");
    const ok = s === "viewed" && m === null;
    return ok
      ? pass(name)
      : fail(name, `state=${s} manifest=${m}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testFinishConsumeFromUnviewedThrows(): Promise<TestCase> {
  const name = "finishConsume from .unviewed throws ViewOnceWrongStateError";
  try {
    const store = freshStore();
    await store.register(sampleManifest("m1"), "incoming");
    try {
      await store.finishConsume("m1");
      return fail(name, "did not throw");
    } catch (e) {
      if (e instanceof ViewOnceWrongStateError && e.actual === "unviewed") {
        return pass(name);
      }
      return fail(name, `wrong error: ${String(e)}`);
    }
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testWipeFromAnyNonTerminalState(): Promise<TestCase> {
  const name = "wipe works from .unviewed / .viewing / .viewed";
  try {
    // From .unviewed
    const s1 = freshStore();
    await s1.register(sampleManifest("m1"), "outgoing");
    await s1.wipe("m1");
    const unviewedOk =
      (await s1.state("m1")) === "wiped" &&
      (await s1.manifest("m1")) === null;

    // From .viewing
    const s2 = freshStore();
    await s2.register(sampleManifest("m2"), "incoming");
    await s2.beginConsume("m2");
    await s2.wipe("m2");
    const viewingOk =
      (await s2.state("m2")) === "wiped" &&
      (await s2.manifest("m2")) === null;

    // From .viewed
    const s3 = freshStore();
    await s3.register(sampleManifest("m3"), "incoming");
    await s3.beginConsume("m3");
    await s3.finishConsume("m3");
    await s3.wipe("m3");
    const viewedOk = (await s3.state("m3")) === "wiped";

    const ok = unviewedOk && viewingOk && viewedOk;
    return ok
      ? pass(name)
      : fail(name, `unviewed=${unviewedOk} viewing=${viewingOk} viewed=${viewedOk}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testWipeIsIdempotent(): Promise<TestCase> {
  const name = "wipe is idempotent";
  try {
    const store = freshStore();
    await store.register(sampleManifest("m1"), "outgoing");
    await store.wipe("m1");
    await store.wipe("m1");
    await store.wipe("m1");
    const ok = (await store.state("m1")) === "wiped";
    return ok ? pass(name) : fail(name, "state is not wiped after triple wipe");
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testMarkExpiredDropsManifest(): Promise<TestCase> {
  const name = "markExpired → .expired, manifest null";
  try {
    const store = freshStore();
    await store.register(sampleManifest("m1"), "incoming");
    await store.markExpired("m1");
    const s = await store.state("m1");
    const m = await store.manifest("m1");
    const ok = s === "expired" && m === null;
    return ok ? pass(name) : fail(name, `state=${s} manifest=${m}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

// ── Hardening ─────────────────────────────────────────────────────────────────

async function testKeyBytesGoneFromIndexedDBAfterFinishConsume(): Promise<TestCase> {
  const name = "finishConsume removes key bytes from IndexedDB JSON";
  try {
    // Use a distinctive pattern so false-negative is impossible.
    const keyBytes = new Uint8Array(32).fill(0xa7);
    const manifest = {
      version: VIEW_ONCE_MANIFEST_VERSION as typeof VIEW_ONCE_MANIFEST_VERSION,
      mediaId: "m1",
      blobUrl: "u",
      mimeType: "image/jpeg",
      byteLength: 1,
      key: keyBytes,
      createdAt: new Date().toISOString(),
    };
    const store = freshStore();
    await store.register(manifest, "incoming");
    await store.beginConsume("m1");
    await store.finishConsume("m1");

    const raw = await store.rawDiskJSON("m1");
    if (raw == null) return fail(name, "rawDiskJSON returned null");

    const keyB64 = uint8ToBase64(keyBytes);
    // Check raw JSON string for the base64 key (the only form it appears in JSON).
    const containsKeyB64 = raw.includes(keyB64);
    const ok = !containsKeyB64;
    return ok
      ? pass(name)
      : fail(name, `raw IndexedDB JSON still contains key base64: ${keyB64.slice(0, 8)}...`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testKeyBytesGoneFromIndexedDBAfterWipe(): Promise<TestCase> {
  const name = "wipe removes key bytes from IndexedDB JSON";
  try {
    const keyBytes = new Uint8Array(32).fill(0x5c);
    const manifest = {
      version: VIEW_ONCE_MANIFEST_VERSION as typeof VIEW_ONCE_MANIFEST_VERSION,
      mediaId: "m1",
      blobUrl: "u",
      mimeType: "image/jpeg",
      byteLength: 1,
      key: keyBytes,
      createdAt: new Date().toISOString(),
    };
    const store = freshStore();
    await store.register(manifest, "outgoing");
    await store.wipe("m1");

    const raw = await store.rawDiskJSON("m1");
    if (raw == null) return fail(name, "rawDiskJSON returned null");

    const keyB64 = uint8ToBase64(keyBytes);
    const containsKeyB64 = raw.includes(keyB64);
    const ok = !containsKeyB64;
    return ok
      ? pass(name)
      : fail(name, `raw IndexedDB JSON still contains key base64 after wipe`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testRecoverInterruptedSessions(): Promise<TestCase> {
  const name = "recoverInterruptedSessions force-completes stale .viewing";
  try {
    // s1 simulates the crash: registers and calls beginConsume but never
    // finishConsume. State on disk is `viewing`.
    const dbName = `kaether_view_once_test_crash_${Date.now()}`;
    const s1 = new ViewOnceStore(dbName);
    await s1.register(sampleManifest("m1"), "incoming");
    await s1.beginConsume("m1"); // crash window: state is `viewing` on disk

    // s2 simulates the next launch — shares the same DB name.
    const s2 = new ViewOnceStore(dbName);
    const recovered = await s2.recoverInterruptedSessions();
    const s = await s2.state("m1");
    const m = await s2.manifest("m1");
    const ok =
      recovered.length === 1 &&
      recovered[0] === "m1" &&
      s === "viewed" &&
      m === null;
    return ok
      ? pass(name)
      : fail(name, `recovered=${JSON.stringify(recovered)} state=${s} manifest=${m}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testManifestAccessorNilAfterConsume(): Promise<TestCase> {
  const name = "manifest accessor returns null after consume";
  try {
    const store = freshStore();
    await store.register(sampleManifest("m1"), "incoming");
    await store.beginConsume("m1");
    await store.finishConsume("m1");
    const m = await store.manifest("m1");
    const ok = m === null;
    return ok ? pass(name) : fail(name, "manifest is not null after finishConsume");
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testStatePersistsAcrossStoreInstances(): Promise<TestCase> {
  const name = "state persists across store re-instantiation";
  try {
    const dbName = `kaether_view_once_test_persist_${Date.now()}`;
    const s1 = new ViewOnceStore(dbName);
    await s1.register(sampleManifest("m1"), "incoming");
    await s1.beginConsume("m1");
    await s1.finishConsume("m1");

    // Create a new instance pointing to the same DB — state must survive.
    const s2 = new ViewOnceStore(dbName);
    const s = await s2.state("m1");
    const m = await s2.manifest("m1");
    const ok = s === "viewed" && m === null;
    return ok
      ? pass(name)
      : fail(name, `state=${s} manifest=${m}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

// ── Coordinator ───────────────────────────────────────────────────────────────

async function testCoordPrepareRegistersOutgoing(): Promise<TestCase> {
  const name = "prepareForSend registers .outgoing in .unviewed";
  try {
    const store = freshStore();
    const coord = new ViewOnceCoordinator(store);
    const { manifest } = await coord.prepareForSend(
      text("hi"),
      "image/jpeg",
      "u",
    );
    const record = await store.record(manifest.mediaId);
    const ok = record?.state === "unviewed" && record.direction === "outgoing";
    return ok
      ? pass(name)
      : fail(name, `state=${record?.state} dir=${record?.direction}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testCoordEndToEndRoundTrip(): Promise<TestCase> {
  const name = "end-to-end: prepare → consume → finishConsume recovers plaintext";
  try {
    const senderStore = freshStore();
    const recipientStore = freshStore();
    const senderCoord = new ViewOnceCoordinator(senderStore);
    const recipientCoord = new ViewOnceCoordinator(recipientStore);

    const plaintext = text("a secret photo");
    const { ciphertext, manifest } = await senderCoord.prepareForSend(
      plaintext,
      "image/jpeg",
      "u",
    );
    // Simulate manifest hand-off via E2EE message body:
    await recipientCoord.registerIncoming(manifest);
    const recovered = await recipientCoord.consume(manifest.mediaId, ciphertext);
    await recipientCoord.finishConsume(manifest.mediaId);

    const s = await recipientStore.state(manifest.mediaId);
    const ok =
      recovered.length === plaintext.length &&
      recovered.every((b, i) => b === plaintext[i]) &&
      s === "viewed";
    return ok
      ? pass(name)
      : fail(name, `recovered=${recovered.length} bytes; state=${s}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testCoordConsumeTwiceThrows(): Promise<TestCase> {
  const name = "consume twice throws (.viewing blocks second reveal)";
  try {
    const store = freshStore();
    const coord = new ViewOnceCoordinator(store);
    const { ciphertext, manifest } = await coord.prepareForSend(
      text("x"),
      "image/jpeg",
      "u",
    );
    await coord.consume(manifest.mediaId, ciphertext);
    // Do NOT call finishConsume — verify second consume is blocked in .viewing.
    try {
      await coord.consume(manifest.mediaId, ciphertext);
      return fail(name, "second consume returned plaintext");
    } catch (e) {
      if (e instanceof ViewOnceWrongStateError) return pass(name);
      return fail(name, `unexpected error: ${String(e)}`);
    }
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testCoordConsumeAfterWipeThrows(): Promise<TestCase> {
  const name = "consume after wipe throws";
  try {
    const store = freshStore();
    const coord = new ViewOnceCoordinator(store);
    const { ciphertext, manifest } = await coord.prepareForSend(
      text("x"),
      "image/jpeg",
      "u",
    );
    await coord.wipeOutgoing(manifest.mediaId);
    try {
      await coord.consume(manifest.mediaId, ciphertext);
      return fail(name, "consume returned plaintext on wiped record");
    } catch (e) {
      if (e instanceof ViewOnceWrongStateError || e instanceof ViewOnceNotFoundError) {
        return pass(name);
      }
      return fail(name, `unexpected error: ${String(e)}`);
    }
  } catch (e) {
    return fail(name, String(e));
  }
}
